import { useCallback, useState } from 'react';
import { useTRPCAPI, useTRPCClientAPI, useTRPCMutation, useTRPCQuery } from '../../../../trpc/trpc';

export interface ApiAccessToken {
  id: number;
  name: string;
  tokenPrefix: string;
  createdAt: Date;
  lastUsedAt: Date | null;
}

/** The plaintext token, back exactly once from `music24ApiTokens.create`. */
export interface CreatedApiToken {
  id: number;
  name: string;
  token: string;
  tokenPrefix: string;
}

/**
 * Everything the API-access card needs: the token list, minting a new one, and revoking one.
 * Platform-agnostic (no React Native import), so the web and native cards share it.
 */
export function useApiAccessTokens() {
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();

  const {
    data,
    isLoading,
    error,
    refetch: refetchUnsafe,
  } = useTRPCQuery(trpcAPI.music24ApiTokens.list.queryOptions(undefined, { retry: false }));

  const refetch = useCallback(() => {
    refetchUnsafe().catch(console.error);
  }, [refetchUnsafe]);

  const [justCreated, setJustCreated] = useState<CreatedApiToken | null>(null);
  const dismissCreated = useCallback(() => setJustCreated(null), []);

  const createMutation = useTRPCMutation({
    mutationFn: (input: { name: string }) => trpcClient.music24ApiTokens.create.mutate(input),
    onSuccess: (result: CreatedApiToken) => {
      setJustCreated(result);
      refetch();
    },
  });

  const [revokingId, setRevokingId] = useState<number | null>(null);
  const revokeMutation = useTRPCMutation({
    mutationFn: (input: { id: number }) => trpcClient.music24ApiTokens.revoke.mutate(input),
    onSuccess: () => {
      setRevokingId(null);
      refetch();
    },
    onError: () => setRevokingId(null),
  });

  const revokeToken = useCallback(
    (id: number) => {
      setRevokingId(id);
      revokeMutation.mutate({ id });
    },
    [revokeMutation]
  );

  return {
    tokens: (data as ApiAccessToken[] | undefined) ?? [],
    isLoading,
    error,
    refetch,
    justCreated,
    dismissCreated,
    createToken: createMutation.mutate,
    isCreating: createMutation.isPending,
    createError: createMutation.error,
    resetCreateError: createMutation.reset,
    revokeToken,
    revokingId,
    isRevoking: revokeMutation.isPending,
  };
}
